import type { AdminCustomerDto, MembershipDto, PageEnvelope } from "@bafe/api-client";
import { AnchoredPanel, cn } from "@bafe/ui";
import { MoreVertical } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Popup } from "./Popup.js";
import { WorkspaceAppsPanel } from "./WorkspaceAppsPanel.js";
import { ConfirmDialog } from "./ConfirmDialog.js";
import { api } from "../lib/api.js";
import { queryKeys } from "../lib/query.js";
import { useOrganisationStore } from "../lib/stores.js";
import { workspaceHref } from "../lib/workspace-url.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const ICON_BUTTON =
	"inline-flex size-10 shrink-0 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-secondary) transition-colors duration-150 hover:bg-(--surface-sunken) hover:text-(--text-primary) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)";

function RowActionsMenu({
	label,
	items,
}: {
	label: string;
	items: Array<{ id: string; label: string; onSelect: () => void; danger?: boolean }>;
}) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuId = useId();

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				className={cn(ICON_BUTTON, open && "bg-(--surface-sunken) text-(--text-primary)")}
				aria-label={label}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen((current) => !current)}
			>
				<MoreVertical className="size-4" aria-hidden="true" />
			</button>
			<AnchoredPanel
				open={open}
				anchorRef={triggerRef}
				align="end"
				onDismiss={() => setOpen(false)}
				id={menuId}
				role="menu"
				aria-label={label}
				className="min-w-44 py-1"
			>
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							item.onSelect();
						}}
						className={cn(
							"flex w-full rounded-(--radius-sm) px-2.5 py-2 text-left text-sm outline-none",
							item.danger ? "text-(--color-critical-700)" : "text-(--text-primary)",
							"hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)",
						)}
					>
						{item.label}
					</button>
				))}
			</AnchoredPanel>
		</>
	);
}

/** Row actions on the signed-in user's workspace list — open only. */
export function WorkspaceMembershipActions({ membership }: { membership: MembershipDto }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);

	const openWorkspace = useMutation({
		mutationFn: () => api.selectWorkspace(membership.organisationId),
		onSuccess: (result) => {
			setOrganisationId(result.organisationId);
			void queryClient.invalidateQueries();
			navigate(workspaceHref(result.organisationSlug, ""));
		},
	});

	const menuItems = [
		{
			id: "open",
			label: "Open",
			onSelect: () => openWorkspace.mutate(),
		},
	];

	return (
		<RowActionsMenu
			label={`Actions for ${membership.organisationName}`}
			items={menuItems}
		/>
	);
}

/** Platform Workspace customer table — operators manage apps and closure. */
export function PlatformCustomerActions({ customer }: { customer: AdminCustomerDto }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [appsOpen, setAppsOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [confirmName, setConfirmName] = useState("");

	const canClose = customer.status !== "closed";

	const closeWorkspace = useMutation({
		mutationFn: () => api.closeWorkspace(customer.organisationId, confirmName.trim()),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: ["admin-customers"] });
			const entries = queryClient.getQueriesData<PageEnvelope<AdminCustomerDto>>({
				queryKey: ["admin-customers"],
			});
			for (const [key, page] of entries) {
				if (!page) continue;
				queryClient.setQueryData<PageEnvelope<AdminCustomerDto>>(key, {
					...page,
					data: page.data.filter((row) => row.organisationId !== customer.organisationId),
					meta: {
						...page.meta,
						total:
							page.meta.total === null
								? null
								: Math.max(0, page.meta.total - 1),
					},
				});
			}
		},
		onSuccess: async () => {
			setDeleteOpen(false);
			setConfirmName("");
			await queryClient.invalidateQueries({ queryKey: queryKeys.session });
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
		},
	});

	const menuItems = [
		{
			id: "open",
			label: "Open",
			onSelect: () => navigate(`/platform/workspace/${customer.organisationId}`),
		},
		{
			id: "apps",
			label: "Apps",
			onSelect: () => setAppsOpen(true),
		},
		...(canClose
			? [
					{
						id: "delete",
						label: "Delete",
						danger: true as const,
						onSelect: () => {
							setConfirmName("");
							setDeleteOpen(true);
						},
					},
				]
			: []),
	];

	return (
		<>
			<RowActionsMenu label={`Actions for ${customer.name}`} items={menuItems} />

			<Popup open={appsOpen} title="Apps" onClose={() => setAppsOpen(false)}>
				<WorkspaceAppsPanel
					organisationId={customer.organisationId}
					organisationName={customer.name}
					canManage
				/>
			</Popup>

			<ConfirmDialog
				open={deleteOpen}
				onClose={() => {
					setDeleteOpen(false);
					setConfirmName("");
					closeWorkspace.reset();
				}}
				title={`Delete ${customer.name}?`}
				description="This closes the workspace for everyone. Mail and domains stay on the server until removed separately."
				confirmLabel="Delete workspace"
				danger
				pending={closeWorkspace.isPending}
				error={closeWorkspace.error}
				confirmPhrase={customer.name}
				confirmPhraseLabel={`Type ${customer.name} to confirm`}
				confirmValue={confirmName}
				onConfirmValueChange={setConfirmName}
				onConfirm={() => closeWorkspace.mutate()}
			/>
		</>
	);
}
